import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { connect } from 'react-redux';
import MainHeader from '../MainHeader/MainHeader';
import MainSidebar, { SidebarSections } from '../MainSidebar/MainSidebar';
import './MainLayout.css';
import envConfig from '../../../envConfig';
import AppPage from '../pages/AppPage/AppPage';
import GenericErrorFallback from '../../common/GenericErrorFallback/GenericErrorFallback';
import {
  selectIsAuthenticated,
  selectSessionInformation,
  selectSubscriptionSwapUrl,
  selectUnreadUserMessages,
  sessionInformationPropType,
  setSubscriptionSwapRedirectUrl,
} from '../../../redux/slices/sessionSlice';
import dispatchAction from '../../../redux/utils/dispatchAction';
import { selectActiveAlertEventList } from '../../../redux/slices/alertEvents/alertEventsSlice';
import { sessionHash } from '../../../utility/remoteServiceUtil';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import LatestAccountMessageBox from '../../common/AccountMessageBox/LatestAccountMessageBox';
import AccountStatusNotificationBar from '../../AccountStatusNotificationBar/AccountStatusNotificationBar';

class MainLayout extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isMenuMinified: false,
    };
    this.onActiveSubscriptionChange = this.onActiveSubscriptionChange.bind(this);
    this.toggleSidebar = this.toggleSidebar.bind(this);
    this.setIsMinified = this.setIsMinified.bind(this);
  }

  componentDidMount() {
    this.setState({ isMenuMinified: window.innerWidth < 979 });
    window.addEventListener(
      'resize',
      this.setIsMinified,
      false,
    );
  }

  componentWillUnmount() {
    window.removeEventListener(
      'resize',
      this.setIsMinified,
      false,
    );
  }

  setIsMinified() {
    this.setState({
      isMenuMinified: window.innerWidth < 979,
    });
  }

  onActiveSubscriptionChange(newSubscriptionId) {
    const { dispatch, history, subscriptionSwapRedirectUrl } = this.props;
    const formData = new FormData();
    formData.append('subscriptionId', newSubscriptionId);
    RaaAuthenticatedRemoteService.globalInstance().post(`${envConfig.raaUrl}/user/data/select-subscription`, { data: formData, nonBearerAuthToken: sessionHash })
      .then(() => {
        if (subscriptionSwapRedirectUrl) {
          history.push(subscriptionSwapRedirectUrl);
        } else {
          window.location.reload();
        }
      }).finally(() => {
        dispatchAction(dispatch, setSubscriptionSwapRedirectUrl, '');
      });
  }

  toggleSidebar() {
    const { isMenuMinified } = this.state;
    this.setState({ isMenuMinified: !isMenuMinified });
  }

  /* istanbul ignore next */
  render() {
    const { isMenuMinified } = this.state;
    const {
      sessionInformation, activeAlertEventList, isAuthenticated, unreadUserMessages,
    } = this.props;
    if (!isAuthenticated) {
      return (<span />);
    }

    const { activeSubscription } = sessionInformation;
    const isUnpaidTier = activeSubscription && activeSubscription.subscriptionTierLevel <= 0;

    const disabledSidebarSections = isUnpaidTier
      ? [
        SidebarSections.DASHBOARD,
        SidebarSections.DEVICES,
        SidebarSections.MONITOR360,
        SidebarSections.ALERTS,
        SidebarSections.ACTIONS,
        SidebarSections.SENSOR_DATA,
        SidebarSections.GUIDANCE,
        SidebarSections.SETUP_CHECKLIST,
      ]
      : [];

    return (
      <div className="main-layout overflow-hidden">
        <div className={`${isMenuMinified ? 'minified' : ''}`}>
          <MainHeader
            emailAddress={sessionInformation.email}
            toggleSidebar={this.toggleSidebar}
            messageCount={unreadUserMessages.length}
          />

          {sessionInformation.activeSubscription && (
            <MainSidebar
              toggleSidebar={this.toggleSidebar}
              isMinified={isMenuMinified}
              onActiveSubscriptionChange={this.onActiveSubscriptionChange}
              userAssignmentList={sessionInformation.userAssignmentList}
              activeSubscription={sessionInformation.activeSubscription}
              alertCount={activeAlertEventList.length}
              disabledSections={disabledSidebarSections}
            />
          )}

          <div id="main">
            <AccountStatusNotificationBar />
            <div id="content" className="container-fluid">

              <ErrorBoundary
                FallbackComponent={GenericErrorFallback}
                onReset={() => window.location.reload()}
                /* eslint-disable-next-line no-console */
                onError={(error) => console.log('error caught at boundary: ', error.message)}
                resetKeys={[window.location.pathname]}
              >
                <LatestAccountMessageBox />
                <AppPage />
              </ErrorBoundary>

            </div>
          </div>
          <footer className="page-footer position-absolute text-right">
            <div className="txt-color-white inline-block">
              <span className="txt-color-blueLight hidden-mobile">
                <a href="/terms" target="_blank">Terms of Service</a>
                {' '}
                | © Copyright
                {' '}
                {new Date().getFullYear()}
                {' '}
                AVTECH Software, Inc.
              </span>
            </div>
          </footer>
        </div>
      </div>
    );
  }
}

MainLayout.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  sessionInformation: sessionInformationPropType.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  subscriptionSwapRedirectUrl: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  unreadUserMessages: PropTypes.arrayOf(PropTypes.any).isRequired,
  activeAlertEventList: PropTypes.arrayOf(
    PropTypes.shape({
      alertId:
      PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }),
  ).isRequired,
};

const mapStateToProps = (state) => ({
  sessionInformation: selectSessionInformation(state),
  subscriptionSwapRedirectUrl: selectSubscriptionSwapUrl(state),
  isAuthenticated: selectIsAuthenticated(state),
  unreadUserMessages: selectUnreadUserMessages(state),
  activeAlertEventList: selectActiveAlertEventList(state),
});

export { MainLayout };
export default withRouter(
  connect(mapStateToProps)((MainLayout)),
);
